import { FaStar } from "react-icons/fa";
import { useInView } from "../../hooks/useInView";

interface Testimonial {
  initials: string;
  avatarBg: string;
  name: string;
  title: string;
  company: string;
  quote: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    initials: "MV",
    avatarBg: "#475569",
    name: "Head of Engineering",
    title: "",
    company: "Orbit Labs",
    quote:
      "We replaced four fragmented tools with Flowbox. It gave our engineering and design teams one single source of truth.",
  },
  {
    initials: "SL",
    avatarBg: "#176766",
    name: "Sarah Lin",
    title: "VP of Product",
    company: "CraftHQ",
    quote:
      "Flowbox cut our cross-functional alignment cycles in half. The visual workflow is intuitive and seamless.",
  },
  {
    initials: "ER",
    avatarBg: "#7c3aed",
    name: "Elena Rostova",
    title: "Design Director",
    company: "Studio Forma",
    quote:
      "Our clients love the transparent progress views. It turned status updates from tedious meetings into effortless async check-ins.",
  },
];

const STATS = [
  { value: "4.9 / 5", label: "Average Rating (2,400+ reviews)" },
  { value: "99.4%", label: "Customer Satisfaction" },
  { value: "40%", label: "Faster Project Delivery" },
  { value: "120k+", label: "Tasks Completed Weekly" },
];

export function Testimonials() {
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <section className="mx-auto w-full max-w-[1334px] px-4 pb-16 pt-10 sm:pb-20 sm:pt-12">
      <div
        ref={ref}
        className={`mx-auto max-w-[512px] text-center ${inView ? "in-view" : ""}`}
      >
        <h2 className="reveal-scroll delay-1 text-3xl font-extrabold leading-[44px] tracking-[-0.5px] text-[#0f172a] sm:text-4xl">
          What they say about us
        </h2>
        <p className="reveal-scroll delay-2 mt-2 text-sm text-[#64748b]">
          Discover how high-velocity leaders and modern remote teams build
          velocity, clarity, and focus with Flowbox.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3">
        {TESTIMONIALS.map((t) => (
          <div
            key={t.name + t.company}
            className="flex flex-col rounded-2xl border border-solid border-[rgba(226,232,240,0.6)] bg-white p-6 shadow-[0px_2px_8px_rgba(62,2,92,0.08)]"
          >
            <div className="flex items-center gap-1 text-[#fbbf24]">
              {Array.from({ length: 5 }).map((_, i) => (
                <FaStar key={i} size={16} />
              ))}
            </div>

            <p className="mt-4 text-sm italic leading-6 text-[#475569]">
              &ldquo;{t.quote}&rdquo;
            </p>

            <div className="mt-6 flex items-center justify-between gap-3 border-t border-solid border-[#e2e8f0] pt-4">
              <div className="flex items-center gap-3">
                <span
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: t.avatarBg }}
                >
                  {t.initials}
                </span>
                <div>
                  <p className="text-sm font-bold text-[#1c1917]">{t.name}</p>
                  {t.title ? (
                    <p className="text-xs text-[#64748b]">{t.title}</p>
                  ) : null}
                </div>
              </div>
              <span className="shrink-0 rounded-full border border-solid border-[#e2e8f0] bg-[#f8fafc] px-3 py-1 text-xs font-medium text-[#475569]">
                {t.company}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-14 grid grid-cols-2 gap-8 border-t border-solid border-[#e2e8f0] pt-10 text-center sm:grid-cols-4">
        {STATS.map((stat) => (
          <div key={stat.label}>
            <p className="text-2xl font-bold text-[#0f172a] sm:text-3xl">
              {stat.value}
            </p>
            <p className="mt-1 text-xs text-[#64748b] sm:text-sm">
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
